// Phase 1 support chatbot — a hand-written FAQ knowledge base, matched
// client-side (no LLM, no backend call). Every entry describes a feature
// that actually exists in the app today; nothing speculative. `roles`
// controls which panel(s) an entry is offered on.
const supportKnowledgeBase = [
  // ── Admin ──────────────────────────────────────────────────────────
  {
    id: 'admin-create-teacher',
    roles: ['admin'],
    question: 'How do I create a teacher?',
    answer: "Go to Admin → Teachers → \"Add Teacher\". Fill in their name, email, and subject, then save — a login is created automatically.",
    keywords: ['create teacher', 'add teacher', 'new teacher', 'teacher account', 'teacher login'],
  },
  {
    id: 'admin-create-student',
    roles: ['admin'],
    question: 'How do I add a single student?',
    answer: "Go to Admin → Students → \"Add Student\". Fill in their details including class and batch, and optionally their Parent Name/Parent WhatsApp for report delivery.",
    keywords: ['add student', 'create student', 'new student', 'student account'],
  },
  {
    id: 'admin-bulk-students',
    roles: ['admin'],
    question: 'How do I bulk import students?',
    answer: "On Admin → Students, use \"Bulk Import\" — download the Excel template, fill in your students (including the Parent Name / Parent WhatsApp columns), then upload it.",
    keywords: ['bulk student', 'import student', 'excel student', 'upload student', 'bulk import'],
  },
  {
    id: 'admin-create-test',
    roles: ['admin'],
    question: 'How do I create a test?',
    answer: "Go to Admin → Tests → \"Create Test\". Choose subject/chapters, set duration and marking scheme, then assign it to a batch.",
    keywords: ['create test', 'new test', 'make test', 'add test'],
  },
  {
    id: 'admin-bulk-questions',
    roles: ['admin'],
    question: 'How do I bulk upload questions and tests?',
    answer: "Admin → Tests → \"Bulk Upload Questions\" lets you upload an Excel/zip of questions and tests together — download the template first to see the required format.",
    keywords: ['bulk upload test', 'bulk question', 'upload question', 'import question', 'bulk upload'],
  },
  {
    id: 'admin-batches',
    roles: ['admin'],
    question: 'How do I manage batches?',
    answer: "Admin → Batches lets you create and manage batches — a batch bundles a class, subject, and its assigned teacher together.",
    keywords: ['batch', 'create batch', 'class batch', 'manage batch'],
  },
  {
    id: 'admin-schedule',
    roles: ['admin'],
    question: 'How do I set the class schedule?',
    answer: "Admin → Schedule lets you set weekly class timings per batch. Double-bookings (same batch or same teacher at an overlapping time) are caught automatically.",
    keywords: ['schedule', 'timetable', 'class timing', 'class schedule'],
  },
  {
    id: 'admin-reports',
    roles: ['admin'],
    question: 'Where do I see test reports?',
    answer: "Admin → Reports: pick a test to see the full class roster with scores, download PDF/Excel, or open an individual student's Detailed Report.",
    keywords: ['report', 'exam report', 'test result', 'score report', 'test report'],
  },
  {
    id: 'admin-whatsapp-individual',
    roles: ['admin'],
    question: 'How do I send a report to one parent via WhatsApp?',
    answer: "Open a student's Detailed Report (Admin → Reports) and click \"Send via WhatsApp\" — it sends the report straight to the parent's registered WhatsApp number.",
    keywords: ['whatsapp report', 'send report whatsapp', 'send to parent', 'whatsapp parent'],
  },
  {
    id: 'admin-whatsapp-bulk',
    roles: ['admin'],
    question: 'How do I send reports to all parents at once?',
    answer: "On the Reports page, after selecting a test, click \"Send All via WhatsApp\" to message every parent on file for that test's whole roster in one go.",
    keywords: ['bulk whatsapp', 'send all whatsapp', 'whatsapp all parents', 'mass whatsapp'],
  },
  {
    id: 'admin-attendance',
    roles: ['admin'],
    question: 'How does teacher attendance work?',
    answer: "Admin → Attendance: configure the school's geofence location/radius, and view the Attendance Summary plus a live punch-in/out log of teachers.",
    keywords: ['attendance', 'geofence', 'punch in', 'punch out', 'teacher attendance'],
  },
  {
    id: 'admin-billing',
    roles: ['admin'],
    question: 'How do I manage fees and accountants?',
    answer: "Admin → Accountants to create accountant logins. Admin → Accounting (Fee Collections / Collections Report / Receipts / Pending Dues) to manage fees directly yourself.",
    keywords: ['accountant', 'fee', 'billing', 'payment', 'collect fee', 'dues'],
  },
  {
    id: 'admin-analytics',
    roles: ['admin'],
    question: 'Where can I see overall performance analytics?',
    answer: "Admin → Analytics shows top performing students and school-wide performance trends.",
    keywords: ['analytics', 'dashboard stat', 'performance overview'],
  },
  {
    id: 'admin-audit-log',
    roles: ['admin'],
    question: 'How do I see who changed what?',
    answer: "Admin → Activity Log shows every admin/accountant action (create, update, delete) with timestamps.",
    keywords: ['audit log', 'activity log', 'who changed', 'audit trail'],
  },
  {
    id: 'admin-schools',
    roles: ['admin'],
    question: 'How do I manage schools?',
    answer: "Admin → Schools lets you manage the schools under your organization.",
    keywords: ['school', 'add school', 'manage school'],
  },

  // ── Teacher ────────────────────────────────────────────────────────
  {
    id: 'teacher-create-test',
    roles: ['teacher'],
    question: 'How do I create a test?',
    answer: "Teacher → Create Test: pick subject/chapters/difficulty, set duration & marks, then assign it to your batch.",
    keywords: ['create test', 'new test', 'make test'],
  },
  {
    id: 'teacher-custom-test',
    roles: ['teacher'],
    question: 'How do I create a custom or mock test?',
    answer: "Teacher → Custom Test lets you build a test by subject mix and question count instead of picking questions one by one — good for mock exams.",
    keywords: ['custom test', 'random test', 'mock test'],
  },
  {
    id: 'teacher-bulk-questions',
    roles: ['teacher'],
    question: 'How do I bulk upload questions?',
    answer: "On My Tests, use the bulk upload option to add many questions and tests at once via an Excel/zip template.",
    keywords: ['bulk question', 'upload question', 'bulk test', 'import question'],
  },
  {
    id: 'teacher-assign-test',
    roles: ['teacher'],
    question: 'How do I assign a test to my batch?',
    answer: "When creating or editing a test, pick the batch(es) it should be assigned to — students in that batch will see it under Assigned Tests.",
    keywords: ['assign test', 'give test', 'batch test'],
  },
  {
    id: 'teacher-students',
    roles: ['teacher'],
    question: 'What can I see on the Students page?',
    answer: "Teacher → Students has 5 tabs: Student List, Performance (pick any student), Weak Students, Top Performers, and Progress (score trend over time).",
    keywords: ['student list', 'my students', 'student performance', 'weak student', 'top student', 'student progress'],
  },
  {
    id: 'teacher-reports',
    roles: ['teacher'],
    question: 'Where do I see test reports?',
    answer: "Teacher → Reports → Test Reports: pick a test to see the roster with scores; click \"Details\" on any student for their full question-level breakdown, or download their Scorecard PDF.",
    keywords: ['test report', 'exam result', 'score report', 'scorecard'],
  },
  {
    id: 'teacher-whatsapp',
    roles: ['teacher'],
    question: 'How do I send a report to a parent via WhatsApp?',
    answer: "From a student's Detailed Report (Teacher → Reports), click \"Send via WhatsApp\" to message their parent directly, or \"Send All via WhatsApp\" to message the whole class roster at once.",
    keywords: ['whatsapp report', 'send report whatsapp', 'send to parent', 'whatsapp parent'],
  },
  {
    id: 'teacher-schedule',
    roles: ['teacher'],
    question: 'Where can I see my class schedule?',
    answer: "Teacher → Schedule shows Today's Classes, the Weekly Timetable, and Upcoming Classes. This is set by your admin and is read-only for teachers.",
    keywords: ['schedule', 'timetable', 'my classes'],
  },
  {
    id: 'teacher-analytics',
    roles: ['teacher'],
    question: 'Where can I see class performance analytics?',
    answer: "Teacher → Analytics shows performance trends across your batches and tests.",
    keywords: ['analytics', 'class performance'],
  },
  {
    id: 'teacher-attendance',
    roles: ['teacher'],
    question: 'How do I punch in/out for attendance?',
    answer: "Your Teacher Dashboard has a Punch In/Out card — your location is checked against the school's configured geofence.",
    keywords: ['attendance', 'punch in', 'punch out'],
  },

  // ── Shared ─────────────────────────────────────────────────────────
  {
    id: 'shared-password',
    roles: ['admin', 'teacher'],
    question: 'How do I change my password?',
    answer: "Go to Profile (bottom of the sidebar) to update your details or change your password.",
    keywords: ['password', 'change password', 'reset password', 'profile'],
  },
  {
    id: 'shared-orientation',
    roles: ['admin', 'teacher'],
    question: 'What can this dashboard do?',
    answer: "Use the sidebar to navigate — every section (Tests, Reports, Students, etc.) covers one part of running your classes. Ask me about a specific task and I'll point you to the right place.",
    keywords: ['what can', 'how does this work', 'help', 'orientation', 'getting started'],
  },
];

export default supportKnowledgeBase;
